/**
 * A heading you can type into.
 *
 * NOTE: `ui/editable-title.tsx` is a DIFFERENT component with a confusingly similar name — the
 * collection board's contentEditable title, which arrived from the publication branch. The two
 * should almost certainly become one; until someone does that deliberately, this is the one the
 * task builder uses (block headers, Flow event cards) and that one is the board's.
 *
 * Not a `TextField` variant, and the difference is not cosmetic: a text field is a control on a
 * form row that always looks like a control, whereas this IS the heading — it sits at the heading's
 * size and weight, on a barely-there ground that says "editable" without turning a title bar into a
 * form. Giving TextField a mode that removes its border, changes its type scale and adds draft
 * state would be a second component hiding inside the first.
 *
 * Two sites had written it out identically: the block name in every editing header, and a Flow
 * event card's title. Both had their own draft state, their own sync effect and their own copy of
 * the Enter/Escape handling — which is where the duplication actually bites, since keyboard
 * behaviour is the part a reader assumes rather than checks.
 *
 * The commit contract, taken from both copies because they already agreed:
 *
 *   - Blur or Enter commits the TRIMMED draft, and only if it actually changed.
 *   - Empty reverts. A heading with no text is not a rename, it is a slip.
 *   - Escape reverts and leaves the field.
 *
 * Validation stays with the caller — only it knows whether a name is taken — so `error` is passed
 * in and `onEdit` lets it clear that error as soon as typing resumes.
 */
import { useEffect, useState } from 'react';

export interface EditableTitleProps {
  /** The committed value. Typing edits a draft; this is what Escape and an empty entry revert to. */
  value: string;
  /** Called with the trimmed draft, only when it differs from `value` and is not empty. */
  onCommit: (value: string) => void;
  /** Fired on every keystroke, so a caller showing an `error` can clear it as typing resumes. */
  onEdit?: () => void;
  /** A caller-owned rejection ("that name is taken"). Colours the field and is announced. */
  error?: string | null;
  ariaLabel: string;
  /**
   * A hard stop, not a validation.
   *
   * The cursor simply stops, which is the signal arriving while the teacher can still act on it —
   * rethinking a name mid-word, rather than being told after the whole thought is typed. A
   * rejection on commit is the same information delivered too late to use.
   */
  maxLength?: number;
  /** `base` for a block's own header, `sm` for a title nested inside a card. */
  size?: 'sm' | 'base';
  className?: string;
}

export function EditableTitle({ value, onCommit, onEdit, error, ariaLabel, maxLength, size = 'base', className = '' }: EditableTitleProps) {
  const [draft, setDraft] = useState(value);
  // Re-syncs when the value changes underneath — an undo, or another editor's change arriving over
  // the collaboration channel.
  useEffect(() => { setDraft(value); }, [value]);

  function commit() {
    const trimmed = draft.trim();
    if (!trimmed) { setDraft(value); return; }
    if (trimmed !== value) onCommit(trimmed);
  }

  return (
    <input
      type='text'
      // `text-prose`, because that is what it is: text ABOUT content rather than content a student
      // is matched against. It shares the family with alt text and transcripts, and tints with them.
      data-field-kind='text-prose'
      value={draft}
      maxLength={maxLength}
      onChange={e => { setDraft(e.target.value); onEdit?.(); }}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
        if (e.key === 'Escape') { setDraft(value); onEdit?.(); e.currentTarget.blur(); }
      }}
      aria-label={ariaLabel}
      aria-invalid={!!error || undefined}
      title={error ?? undefined}
      className={`min-w-0 truncate rounded-md bg-black/[0.04] px-1.5 py-0.5 -my-0.5 -mx-1.5 font-semibold border-b-2 transition-colors hover:bg-black/[0.07] focus:bg-black/[0.04] dark:bg-white/[0.06] dark:hover:bg-white/[0.10] dark:focus:bg-white/[0.06] ${
        size === 'sm' ? 'text-sm' : 'text-base'
      } ${error ? 'border-ws-error-fg text-ws-error-fg' : 'border-transparent'} ${className}`}
    />
  );
}
